import React, { useState, useEffect } from "react";
import useSWR from "swr";
import { api, formatApiError } from "@/lib/api";
import { FileText, Zap, Mic, Layout, Clock, Loader2, CheckCircle2, XCircle, Mail, Lock, Circle, Wifi, Users, Video, Puzzle, Server, RefreshCw, ShieldCheck, X } from "lucide-react";

const fetcher = (u) => api.get(u).then((r) => r.data);

// Canonical ordered steps of the souvera.provision job (must match the `step`
// labels emitted by core/souvera_provision.py log_fn/progress_fn).
const SOUVERA_STEPS = [
    ["s3", "S3 bucket (Garage)"],
    ["db", "MariaDB (metadata)"],
    ["vm", "Stalwart VM"],
    ["wait", "VM boot / Mgmt API"],
    ["bootstrap", "Mail domain + mailbox"],
    ["pmg", "PMG inbox"],
    ["apps", "Wire up NC apps"],
    ["done", "Done"],
];

// Per-check colour for the Mail-Health panel dots.
const HEALTH_DOT = {
    ok: "bg-emerald-400",
    configured: "bg-sky-400",
    active: "bg-emerald-400",
    error: "bg-red-400",
    critical: "bg-red-400",
    degraded: "bg-amber-400",
    off: "bg-zinc-600",
    unknown: "bg-amber-400",
};
const healthDot = (s) => HEALTH_DOT[s] || "bg-amber-400";

/**
 * Mail-Health — on-demand aggregate status (smail / OIDC / Stalwart-VM / LB
 * HAProxy route) for a live Souvera cloud. Runs guest-exec probes server-side,
 * so it is fetched on demand (manual refresh) rather than auto-polled.
 */
function MailHealthBlock({ cloudId }) {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [lbBusy, setLbBusy] = useState(false);
    const [lbMsg, setLbMsg] = useState(null);
    const [repairBusy, setRepairBusy] = useState(false);
    const [repairMsg, setRepairMsg] = useState(null);
    const [updateChannel, setUpdateChannel] = useState("dev");
    const check = async () => {
        setBusy(true); setErr(null);
        try {
            const r = await api.get(`/api/admin/clouds/${cloudId}/services/mail/health`);
            setData(r.data);
        } catch (e) { setErr(formatApiError(e)); }
        setBusy(false);
    };
    const rebuildLb = async () => {
        if (!window.confirm(
            "Rebuild LB mail routing?\n\nRegenerates the HAProxy config from " +
            "ALL Souvera clouds and applies it to every ready Caddy LB " +
            "(installs HAProxy if needed, opens 993/995/465/4190, " +
            "synchronizes certificates, reload). Idempotent — existing " +
            "routes are preserved, Caddy is NOT touched.")) return;
        setLbBusy(true); setLbMsg(null);
        try {
            const r = await api.post(`/api/admin/infra/souvera-mail-lb/sync`);
            setLbMsg(`OK — ${r.data.routes} route(s) applied to ${r.data.applied} LB(s).`);
            await check();
        } catch (e) { setLbMsg(formatApiError(e)); }
        setLbBusy(false);
    };
    useEffect(() => {
        check();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cloudId]);

    const repairPlugin = async () => {
        if (!window.confirm(
            "Repair Souvera Mail plugin?\n\nCopies the missing Smail engine " +
            "plugin ('nextcloud') onto every affected worker/web VM and ensures " +
            "the [plugins] enable flag. Idempotent & non-destructive — " +
            "user data is not touched.")) return;
        setRepairBusy(true); setRepairMsg(null);
        try {
            const r = await api.post(`/api/admin/clouds/${cloudId}/services/mail/plugin-repair`);
            const n = (r.data.healed || []).length;
            const st = (r.data.health || {}).status;
            setRepairMsg(`Repair done — ${n} VM(s) treated, status→${st || "?"}.`);
            await check();
        } catch (e) { setRepairMsg(formatApiError(e)); }
        setRepairBusy(false);
    };

    const pluginStatus = data?.plugin_status;
    const pluginBroken = pluginStatus === "critical" || pluginStatus === "degraded";

    const rows = [
        ["souvera_mail", "Souvera Mail"],
        ["oidc", "OIDC-Provider (NC)"],
        ["stalwart", "Stalwart-VM"],
        ["haproxy", "Mail-Ports (LB)"],
    ];
    return (
        <div className="mt-2 border border-[#3a2a5a] bg-black/30 rounded p-2" data-testid="souvera-mail-health">
            <div className="flex items-center justify-between mb-1.5">
                <span className="text-[11px] text-zinc-500 uppercase tracking-wide">Mail-Health</span>
                <div className="flex items-center gap-1.5">
                    <button
                        data-testid="mail-health-rebuild-lb-btn"
                        onClick={rebuildLb}
                        disabled={lbBusy}
                        title="Rebuild HAProxy mail routing on the LB from all Souvera clouds (idempotent, no Caddy/route loss)"
                        className="text-[10px] px-2 py-0.5 border border-[#5a3a7a] text-[#b88aff] hover:bg-[#1a0f2a] disabled:opacity-50"
                    >
                        {lbBusy ? "building…" : "Rebuild LB"}
                    </button>
                    <button
                        data-testid="mail-health-refresh-btn"
                        onClick={check}
                        disabled={busy}
                        title="Check souvera_mail / OIDC / Stalwart / LB route live (occ souvera_mail:status + HAProxy grep)"
                        className="text-[10px] px-2 py-0.5 border border-[#3a5a7a] text-[#88c0ff] hover:bg-[#0f1a2a] disabled:opacity-50"
                    >
                        {busy ? "checking…" : "check"}
                    </button>
                </div>
            </div>
            {lbMsg && <div className="text-[11px] text-zinc-400 mb-1" data-testid="mail-health-lb-msg">{lbMsg}</div>}
            {err && <div className="text-[11px] text-red-400" data-testid="mail-health-error">{err}</div>}
            {!data && !err && <div className="text-[11px] text-zinc-600">…</div>}
            {data && !data.souvera && (
                <div className="text-[11px] text-zinc-500">{data.detail}</div>
            )}
            {data && data.souvera && (
                <div className="space-y-1" data-testid="mail-health-rows">
                    {rows.map(([key, label]) => {
                        const c = data[key] || {};
                        const st = c.status || "unknown";
                        return (
                            <div key={key} className="flex items-center gap-2 text-xs"
                                data-testid={`mail-health-${key}`} data-status={st}>
                                <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${healthDot(st)}`} />
                                <span className="text-zinc-300 w-[150px] flex-shrink-0">{label}</span>
                                <span className="text-zinc-500 font-mono uppercase text-[10px]">{st}</span>
                                {key === "stalwart" && c.ip && <span className="text-zinc-600 font-mono text-[10px]">{c.ip}</span>}
                                {key === "haproxy" && c.host && <span className="text-zinc-600 font-mono text-[10px]">{c.host}</span>}
                            </div>
                        );
                    })}
                    {data.souvera_mail && data.souvera_mail.status !== "ok" && data.souvera_mail.detail && (
                        <div className="text-[10px] text-zinc-600 mt-1 break-all" data-testid="mail-health-smail-detail">
                            souvera_mail: {typeof data.souvera_mail.detail === "string" ? data.souvera_mail.detail : JSON.stringify(data.souvera_mail.detail).slice(0, 200)}
                        </div>
                    )}

                    {/* Per-VM Smail engine plugin / NAS health — the AUTHENTICATIONFAILED
                        root cause lives on the WEB VMs (plugin missing → engine sends the
                        literal sentinel). */}
                    <div className="mt-2 pt-2 border-t border-[#3a2a5a]" data-testid="mail-health-plugin-block">
                        <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2 text-xs">
                                <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${healthDot(pluginStatus || "unknown")}`} />
                                <span className="text-zinc-300">Engine plugin {data?.shared ? "(NAS-shared)" : "(per VM)"}</span>
                                <span className="text-zinc-500 font-mono uppercase text-[10px]" data-testid="mail-health-plugin-status">{pluginStatus || "unknown"}</span>
                            </div>
                            <button
                                data-testid="mail-health-repair-btn"
                                onClick={repairPlugin}
                                disabled={repairBusy || !pluginBroken}
                                title="Re-copy the missing Smail engine plugin onto affected VMs + ensure [plugins] enable (idempotent, non-destructive)"
                                className="text-[10px] px-2 py-0.5 border border-[#7a3a3a] text-[#ff9a9a] hover:bg-[#2a0f0f] disabled:opacity-40"
                            >
                                {repairBusy ? "repairing…" : "Repair"}
                            </button>
                        </div>
                        {data.plugin_summary && (
                            <div className="text-[10px] text-zinc-500 mb-1" data-testid="mail-health-plugin-summary">{data.plugin_summary}</div>
                        )}
                        {repairMsg && <div className="text-[11px] text-zinc-400 mb-1" data-testid="mail-health-repair-msg">{repairMsg}</div>}
                        <div className="space-y-1" data-testid="mail-health-vm-rows">
                            {(data.vms || []).map((vm) => (
                                <div key={vm.vmid} className="flex items-center gap-2 text-[11px]"
                                    data-testid={`mail-health-vm-${vm.vmid}`} data-status={vm.status}>
                                    <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${healthDot(vm.status)}`} />
                                    <span className="text-zinc-400 w-[40px] flex-shrink-0 uppercase">{vm.role}</span>
                                    <span className="text-zinc-600 font-mono w-[42px] flex-shrink-0">#{vm.vmid}</span>
                                    <span className="text-zinc-500 font-mono uppercase text-[10px] w-[58px] flex-shrink-0">{vm.status}</span>
                                    <span className="text-zinc-600 text-[10px] truncate">
                                        {vm.status === "ok"
                                            ? (vm.shared_mode ? "Plugin OK · NAS-shared" : "Plugin OK · local")
                                            : (vm.issues || []).join(" · ")}
                                    </span>
                                </div>
                            ))}
                            {(data.vms || []).length === 0 && (
                                <div className="text-[10px] text-zinc-600">No VM data (cluster unreachable?).</div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}


export function SouveraServiceStatusLine({ cloudId }) {
    const { data } = useSWR(
        `/api/admin/clouds/${cloudId}/services/souvera-workspace`,
        fetcher,
        { refreshInterval: 15000 },
    );
    if (!data) return null;
    const statusCfg = {
        active: { cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", label: "Active" },
        provisioning: { cls: "bg-blue-500/15 text-blue-300 border-blue-500/30", label: "Provisioning…" },
        requested: { cls: "bg-zinc-500/15 text-zinc-300 border-zinc-600/40", label: "Requested" },
        off: { cls: "bg-zinc-500/15 text-zinc-400 border-zinc-700/30", label: "Off" },
    }[data.status] || { cls: "bg-zinc-500/15 text-zinc-300 border-zinc-600/40", label: data.status || "—" };
    return (
        <div className="bg-zinc-950 border border-[#3a2a5a] p-3" data-testid="cloud-service-souvera-status">
            <div className="flex items-start gap-2">
                <Mail size={14} className="text-[#b88aff] mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm text-zinc-200">Souvera Workspace</span>
                        <span className={`cm-badge ${statusCfg.cls} text-[10px]`} data-testid="souvera-status-badge-compact">
                            {statusCfg.label}
                        </span>
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-1 leading-relaxed">
                        Own Stalwart mail server + Souvera apps (Central, Shield, Mail, Calendar, Contacts).
                    </div>
                    {data.status === "active" && data.souvera_domain && (
                        <div className="text-[11px] text-zinc-400 font-mono mt-1">
                            {data.souvera_domain}
                            {data.mail_domain && <span> · Mail: <span className="text-zinc-200">{data.mail_domain}</span></span>}
                            {data.souvera_user != null && <span className="text-zinc-500"> · {data.souvera_user} users</span>}
                            {data.souvera_users_current != null && (
                                <span className="text-zinc-500"> ({data.souvera_users_current} in use)</span>
                            )}
                        </div>
                    )}
                    {data.error && (
                        <div className="text-[11px] text-red-400 font-mono mt-1">{data.error}</div>
                    )}
                </div>
            </div>
        </div>
    );
}


/**
 * Souvera Workspace — gated premium service (own Stalwart mail server + Souvera
 * apps). Only rendered when the cloud's reseller holds the `souvera_workspace`
 * entitlement (server-decided via `eligible`). Requires a freely-chosen mail
 * domain (independent of the NC domains) on enable.
 */
export function SouveraWorkspaceLine({ cloudId, cloud, onChange, compact = false }) {
    const { data, mutate } = useSWR(
        `/api/admin/clouds/${cloudId}/services/souvera-workspace`,
        fetcher,
        { refreshInterval: 300000, keepPreviousData: true },
    );
    const { data: pipe } = useSWR(
        `/api/admin/clouds/${cloudId}/services/souvera-workspace/pipeline`,
        fetcher,
        { refreshInterval: data?.status === "provisioning" ? 4000 : 0 },
    );
    const [domain, setDomain] = useState("");
    const [slug, setSlug] = useState("");      // {slug}.souvera.work → primary domain
    const [link, setLink] = useState(false);   // Souvera Link → Talk/HPB
    const [desk, setDesk] = useState(false);   // Souvera Desk → Collabora
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    // Souvera is sold per user (Collabora included per seat). Captured at
    // activation, editable afterwards via /service-seats. Stored only — no
    // enforcement yet.
    const [souveraSeats, setSouveraSeats] = useState("");
    const [seatBusy, setSeatBusy] = useState(false);
    useEffect(() => { setSouveraSeats(data?.souvera_user ?? ""); }, [data?.souvera_user]);
    // Gesamt-Mail-Speicher (GB) — Pflichtfeld bei Aktivierung (Default 50),
    // danach editierbar (occ souvera_central:mailstorage:set); der Kunde
    // verteilt die Gesamt-Quota in Souvera Central auf die Postfächer.
    const [mailQuota, setMailQuota] = useState("50");
    const [quotaBusy, setQuotaBusy] = useState(false);
    useEffect(() => { setMailQuota(String(data?.mail_quota_gb ?? 50)); }, [data?.mail_quota_gb]);
    const saveQuota = async () => {
        setQuotaBusy(true);
        setErr(null);
        try {
            await api.post(`/api/admin/clouds/${cloudId}/services/souvera-workspace/mail-quota`,
                { quota_gb: mailQuota === "" ? null : Number(mailQuota) });
            await mutate();
            onChange && (await onChange());
        } catch (e) {
            setErr(formatApiError(e));
        }
        setQuotaBusy(false);
    };
    const saveSeats = async () => {
        setSeatBusy(true);
        setErr(null);
        try {
            // Persistiert souvera_user UND wendet occ souvera_central:licenses:set
            // an (via souvera.licenses-Job). Nur für Souvera.
            await api.post(`/api/admin/clouds/${cloudId}/services/souvera-workspace/licenses`,
                { seats: souveraSeats === "" ? null : Number(souveraSeats) });
            await mutate();
            onChange && (await onChange());
        } catch (e) {
            setErr(formatApiError(e));
        }
        setSeatBusy(false);
    };

    // Live step progress: subscribe to the souvera.provision job's SSE stream
    // (same stream the Job detail page uses). We accumulate which steps have
    // logged and the current step, then derive a per-step status below.
    const souveraStep = (pipe?.steps || []).find((s) => s.step === "souvera-workspace");
    const jobId = souveraStep?.job_id || null;
    const [stream, setStream] = useState(null);
    useEffect(() => {
        if (!jobId) { setStream(null); return; }
        const base = process.env.REACT_APP_BACKEND_URL || "";
        const es = new EventSource(`${base}/api/admin/jobs/${jobId}/stream`,
            { withCredentials: true });
        const reached = new Set();
        let cur = null, jobStatus = null;
        const apply = () => setStream({ reached: new Set(reached), cur, jobStatus });
        es.addEventListener("log", (e) => {
            try {
                const d = JSON.parse(e.data);
                if (d.step) { reached.add(d.step); cur = d.step; apply(); }
            } catch { /* ignore */ }
        });
        es.addEventListener("status", (e) => {
            try {
                const s = JSON.parse(e.data);
                jobStatus = s.status;
                if (s.current_step) cur = s.current_step;
                apply();
            } catch { /* ignore */ }
        });
        es.addEventListener("end", () => es.close());
        es.onerror = () => es.close();
        return () => es.close();
    }, [jobId]);

    if (!data) return null; // available on every cloud once status loads
    const cloudActive = cloud?.status === "active";
    const slugNorm = slug.trim().toLowerCase().replace(/[^a-z0-9-]/g, "").replace(/^-+|-+$/g, "");
    const slugHost = slugNorm ? `${slugNorm}.souvera.work` : "";

    const enabled = !!data.enabled;
    const status = data.status || "off";
    const statusCfg = {
        requested: { cls: "cm-badge-warning", label: "REQUESTED" },
        provisioning: { cls: "cm-badge-info", label: "PROVISIONING…" },
        deprovisioning: { cls: "cm-badge-info", label: "REMOVING…" },
        active: { cls: "cm-badge-success", label: "ACTIVE" },
        failed: { cls: "cm-badge-danger", label: "ERROR" },
        disabled: { cls: "cm-badge-neutral", label: "OFF" },
        off: { cls: "cm-badge-neutral", label: "OFF" },
    }[status] || { cls: "cm-badge-neutral", label: status.toUpperCase() };
    const inFlight = status === "provisioning" || status === "deprovisioning";

    // Erweitert-Tab: sobald aktiviert, nur noch eine schlichte Service-Zeile
    // (wie Antivirus & Co.) — Details, Health & Aktionen leben im Souvera-Tab.
    // Solange NICHT aktiviert, zeigt die kompakte Zeile weiter das
    // Aktivierungs-Formular (der Souvera-Tab existiert dann noch nicht).
    if (compact && enabled) {
        return (
            <div className="bg-zinc-950 border border-zinc-800 p-3" data-testid="cloud-service-souvera-workspace">
                <div className="flex items-start gap-2">
                    <Mail size={14} className="text-[#b88aff] mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-sm text-zinc-200">Souvera Workspace</span>
                            <span className={`cm-badge ${statusCfg.cls} text-[10px]`} data-testid="souvera-status-badge">
                                {statusCfg.label}
                            </span>
                        </div>
                        <div className="text-[11px] text-zinc-500 mt-1 leading-relaxed">
                            Own Stalwart mail server + Souvera apps. Details, health &amp;
                            actions in the <span className="text-[#b88aff]">Souvera</span> tab.
                        </div>
                    </div>
                    <span
                        data-testid="souvera-locked-badge"
                        title="Souvera Workspace cannot be disabled after activation"
                        className="text-[11px] px-3 py-1.5 border border-zinc-700 text-zinc-500 flex items-center gap-1 flex-shrink-0"
                    >
                        <Lock size={11} /> {inFlight ? "…" : "Locked"}
                    </span>
                </div>
            </div>
        );
    }

    const enable = async () => {
        setBusy(true);
        setErr(null);
        try {
            await api.post(`/api/admin/clouds/${cloudId}/services/souvera-workspace/enable`,
                { mail_domain: domain, slug: slugNorm, souvera_link: link, souvera_desk: desk,
                  souvera_user: souveraSeats === "" ? null : Number(souveraSeats),
                  mail_quota_gb: mailQuota === "" ? null : Number(mailQuota) });
            await mutate();
            onChange && (await onChange());
        } catch (e) {
            setErr(formatApiError(e));
        }
        setBusy(false);
    };
    const pmgResync = async () => {
        setBusy(true);
        setErr(null);
        try {
            await api.post(`/api/admin/clouds/${cloudId}/services/souvera-workspace/pmg-resync`);
            await mutate();
        } catch (e) {
            setErr(formatApiError(e));
        }
        setBusy(false);
    };
    const reprovision = async () => {
        if (!window.confirm(
            "Restart Souvera provisioning?\n\nIdempotent — existing " +
            "S3 bucket, the MariaDB and a reachable Stalwart VM are " +
            "reused; an orphaned/half-finished VM is replaced. No " +
            "manual cleanup needed.")) return;
        setBusy(true);
        setErr(null);
        try {
            await api.post(`/api/admin/clouds/${cloudId}/services/souvera-workspace/reprovision`);
            await mutate();
            onChange && (await onChange());
        } catch (e) {
            setErr(formatApiError(e));
        }
        setBusy(false);
    };
    const dmarcRegister = async () => {
        setBusy(true);
        setErr(null);
        try {
            await api.post(`/api/admin/clouds/${cloudId}/services/souvera-workspace/dmarc/register`);
            await mutate();
        } catch (e) {
            setErr(formatApiError(e));
        }
        setBusy(false);
    };
    const dmarcVerify = async () => {
        setBusy(true);
        setErr(null);
        try {
            await api.post(`/api/admin/clouds/${cloudId}/services/souvera-workspace/dmarc/verify`);
            await mutate();
        } catch (e) {
            setErr(formatApiError(e));
        }
        setBusy(false);
    };
    const updateApps = async () => {
        const label = updateChannel === "stable" ? "Release (stable)" : "Dev (main)";
        if (!window.confirm(
            `Update Souvera apps? (${label})\n\n` +
            "souvera_central / souvera_shield / souvera_mail are installed on the worker, " +
            "migrated (occ upgrade) and activated. " +
            "Stalwart, DNS and mail remain untouched.")) return;
        setBusy(true);
        setErr(null);
        try {
            await api.post(`/api/admin/clouds/${cloudId}/services/souvera-workspace/update-apps?channel=${updateChannel}`);
            await mutate();
            onChange && (await onChange());
        } catch (e) {
            setErr(formatApiError(e));
        }
        setBusy(false);
    };
    const postmasterBackfill = async () => {
        if (!window.confirm(
            "Run postmaster backfill?\n\n" +
            "• postmaster@<domain> app mailbox (500 MB) + send-capable no-reply alias\n" +
            "• scadmin@ mailbox quota → 500 MB\n" +
            "• NC default mailout via Stalwart (sender no-reply@)\n" +
            "• App password in Souvera Central + set total mail storage\n\n" +
            "Idempotent & fault-tolerant — tolerates an already-existing " +
            "postmaster@ (without alias).")) return;
        setBusy(true);
        setErr(null);
        try {
            await api.post(`/api/admin/clouds/${cloudId}/services/souvera-workspace/postmaster-backfill`);
            await mutate();
            onChange && (await onChange());
        } catch (e) {
            setErr(formatApiError(e));
        }
        setBusy(false);
    };
    const hardenAdmins = async () => {
        if (!window.confirm(
            "Sync Souvera admin & groups?\n\n" +
            "• Technical ncadmin is hardened (new internal NC login password)\n" +
            "• Customer admin scadmin is created/repaired (group souvera-admins, NOT superadmin)\n" +
            "• Desk/Collabora & Link/Talk are restricted to souvera-users + souvera-admins " +
            "(or disabled if not booked)\n\nIdempotent — safe to repeat.")) return;
        setBusy(true);
        setErr(null);
        try {
            await api.post(`/api/admin/clouds/${cloudId}/services/souvera-workspace/harden-admins`);
            await mutate();
            onChange && (await onChange());
        } catch (e) {
            setErr(formatApiError(e));
        }
        setBusy(false);
    };
    const mailMigrate = async () => {
        const url = window.prompt(
            "Stalwart mail migration (legacy Souvera v0.13 → v0.16)\n\n" +
            "A NEW Stalwart DB + a NEW S3 bucket are created and the legacy " +
            "data (MySQL + S3 blobs) is copied in — the SOURCE remains " +
            "UNTOUCHED (read-only). The copy then migrates " +
            "v0.13→v0.14→v0.15→v0.16 and CM adds the final config (relay/OIDC). " +
            "Idempotent/resumable.\n\n" +
            "Enter the URL of the Souvera backup ZIP from the legacy cluster:");
        if (!url || !url.trim()) return;
        setBusy(true);
        setErr(null);
        try {
            await api.post(
                `/api/admin/clouds/${cloudId}/services/souvera-workspace/mail-migrate`,
                { source_url: url.trim() });
            await mutate();
            onChange && (await onChange());
        } catch (e) {
            setErr(formatApiError(e));
        }
        setBusy(false);
    };
    // ncadmin credentials moved to a dedicated modal on the cloud header
    // (NcAdminCredentialsModal, opened next to "Credentials").
    // Derive a per-step status from the SSE stream (reached steps + current
    // step + terminal job status) against the canonical SOUVERA_STEPS order.
    const stepperVisible = !!jobId && (status === "provisioning"
        || stream?.jobStatus === "running" || stream?.jobStatus === "failed"
        || status === "failed");
    const derivedSteps = (() => {
        const keys = SOUVERA_STEPS.map((s) => s[0]);
        const reached = stream?.reached || new Set();
        const reachedIdx = keys
            .map((k, i) => (reached.has(k) ? i : -1)).filter((i) => i >= 0);
        const curIdx = reachedIdx.length ? Math.max(...reachedIdx) : -1;
        const js = stream?.jobStatus;
        const succeeded = js === "succeeded";
        const failed = js === "failed" || js === "canceled";
        return SOUVERA_STEPS.map(([key, label], i) => {
            let st;
            if (succeeded) st = "done";
            else if (i < curIdx) st = "done";
            else if (i === curIdx) st = failed ? "error" : "running";
            else st = "pending";
            return { key, label, status: st };
        });
    })();

    return (
        <div className="bg-zinc-950 border border-[#3a2a5a] p-3" data-testid="cloud-service-souvera-workspace">
            <div className="flex items-start gap-2">
                <Mail size={14} className="text-[#b88aff] mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm text-zinc-200">Souvera Workspace</span>
                        <span className={`cm-badge ${statusCfg.cls} text-[10px]`} data-testid="souvera-status-badge">
                            {statusCfg.label}
                        </span>
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-1 leading-relaxed">
                        Own Stalwart mail server + Souvera apps (Central, Shield, Mail,
                        Calendar, Contacts). Every NC user automatically gets
                        <span className="text-zinc-300"> user@mail-domain</span>.
                    </div>
                    {enabled ? (
                        <div className="text-[11px] text-zinc-400 font-mono mt-1.5" data-testid="souvera-mail-domain">
                            {data.souvera_domain && (
                                <div className="mb-0.5" data-testid="souvera-domain">
                                    Primary domain: <span className="text-[#b88aff]">{data.souvera_domain}</span>
                                </div>
                            )}
                            Mail domain: <span className="text-zinc-200">{data.mail_domain}</span>
                            {" · "}admin: <span className="text-zinc-200">{data.stalwart_admin_user || "admin"}</span>
                            {data.ncadmin_email && <span> · ncadmin: <span className="text-zinc-200">{data.ncadmin_email}</span></span>}
                            {data.scadmin_user && (
                                <span data-testid="souvera-swadmin-user">
                                    {" · Customer admin: "}
                                    <span className="text-[#b88aff]">{data.scadmin_user}</span>
                                    <span className="text-zinc-600"> (credentials via “Credentials”)</span>
                                </span>
                            )}
                            {data.stalwart_ip && <span> · {data.stalwart_ip}:8080</span>}
                            {data.status === "active" && (
                                <span data-testid="souvera-pmg-status">
                                    {" · Inbox: "}
                                    <span className={data.pmg_configured ? "text-emerald-400" : "text-amber-400"}>
                                        {data.pmg_configured ? "PMG active" : "PMG open"}
                                    </span>
                                </span>
                            )}
                            {data.status === "active" && (
                                <span data-testid="souvera-apps-status">
                                    {" · NC apps: "}
                                    <span className={data.apps_configured ? "text-emerald-400" : "text-amber-400"}>
                                        {data.apps_configured ? "wired" : "open"}
                                    </span>
                                </span>
                            )}
                            <div className="flex items-center gap-2 mt-1.5" data-testid="souvera-seats-edit-row">
                                <span className="text-zinc-500">Sold users:</span>
                                <input
                                    type="number"
                                    min="0"
                                    data-testid="souvera-seats-input-edit"
                                    className="cm-input text-xs w-24 py-0.5"
                                    placeholder="–"
                                    value={souveraSeats}
                                    onChange={(e) => setSouveraSeats(e.target.value)}
                                />
                                <button
                                    data-testid="souvera-seats-save-btn"
                                    onClick={saveSeats}
                                    disabled={seatBusy || inFlight || !(Number(souveraSeats) >= 1)}
                                    className="text-[11px] px-2 py-0.5 border border-zinc-700 text-zinc-300 hover:bg-zinc-900 disabled:opacity-50"
                                >
                                    {seatBusy ? "…" : "Apply"}
                                </button>
                                <span className="text-zinc-600">Collabora incl. · occ licenses:set</span>
                                {data.souvera_users_current != null && (
                                    <span className="text-zinc-500" data-testid="souvera-current-users">
                                        · currently in use: <span className="text-zinc-300">{data.souvera_users_current}</span>
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-2 mt-1.5" data-testid="souvera-mail-quota-edit-row">
                                <span className="text-zinc-500">Total mail storage (GB):</span>
                                <input
                                    type="number"
                                    min="1"
                                    data-testid="souvera-mail-quota-input-edit"
                                    className="cm-input text-xs w-24 py-0.5"
                                    placeholder="50"
                                    value={mailQuota}
                                    onChange={(e) => setMailQuota(e.target.value)}
                                />
                                <button
                                    data-testid="souvera-mail-quota-save-btn"
                                    onClick={saveQuota}
                                    disabled={quotaBusy || inFlight}
                                    className="text-[11px] px-2 py-0.5 border border-zinc-700 text-zinc-300 hover:bg-zinc-900 disabled:opacity-50"
                                >
                                    {quotaBusy ? "…" : "Apply"}
                                </button>
                                <span className="text-zinc-600">occ quota:default + :set --all</span>
                            </div>
                        </div>
                    ) : (
                        <div className="mt-2 space-y-2">
                            <div className="flex items-center gap-2 flex-wrap">
                                <input
                                    data-testid="souvera-mail-domain-input"
                                    className="cm-input text-xs max-w-[220px] py-1"
                                    placeholder="Mail domain e.g. customer-domain.com"
                                    value={domain}
                                    onChange={(e) => setDomain(e.target.value)}
                                />
                                {!cloudActive && (
                                    <span className="text-[11px] text-amber-400" data-testid="souvera-needs-active">
                                        Cloud must be active
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <input
                                    data-testid="souvera-slug-input"
                                    className="cm-input text-xs max-w-[180px] py-1"
                                    placeholder="Slug e.g. customer-01"
                                    value={slug}
                                    onChange={(e) => setSlug(e.target.value)}
                                />
                                <span className="text-[11px] text-zinc-400 font-mono" data-testid="souvera-slug-preview">
                                    Primary domain → <span className="text-[#b88aff]">{slugHost || "<slug>.souvera.work"}</span>
                                </span>
                            </div>
                            <span className="text-[11px] text-amber-400/90 leading-relaxed block" data-testid="souvera-slug-warning">
                                On activation the cloud's primary domain is switched to
                                {" "}<span className="font-mono text-amber-300">{slugHost || "slug.souvera.work"}</span>{" "}
                                (replaces the current domain). Souvera cannot be disabled afterwards.
                            </span>
                            <div className="flex flex-col gap-1.5">
                                <span className="text-[11px] text-zinc-500 uppercase tracking-wide">Optional add-ons</span>
                                <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer" data-testid="souvera-link-label">
                                    <input type="checkbox" data-testid="souvera-link-checkbox"
                                        className="accent-[#7a5aff]"
                                        checked={link} onChange={(e) => setLink(e.target.checked)} />
                                    <span><span className="text-[#b88aff] font-medium">Souvera Link</span> — Talk/HPB (audio/video calls). Enables the Talk HPB service.</span>
                                </label>
                                <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer" data-testid="souvera-desk-label">
                                    <input type="checkbox" data-testid="souvera-desk-checkbox"
                                        className="accent-[#7a5aff]"
                                        checked={desk} onChange={(e) => setDesk(e.target.checked)} />
                                    <span><span className="text-[#b88aff] font-medium">Souvera Desk</span> — Collabora Online (office/documents). Enables the Collabora service.</span>
                                </label>
                                <span className="text-[11px] text-zinc-600">Both optional — without selection: only mail + Souvera apps (Central, Shield, Mail, Calendar, Contacts).</span>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap" data-testid="souvera-seats-row">
                                <span className="text-[11px] text-zinc-500">Sold users (Souvera):</span>
                                <input
                                    type="number"
                                    min="0"
                                    data-testid="souvera-seats-input"
                                    className="cm-input text-xs w-24 py-1"
                                    placeholder="e.g. 25"
                                    value={souveraSeats}
                                    onChange={(e) => setSouveraSeats(e.target.value)}
                                />
                                <span className="text-[11px] text-amber-400/80">Required · Collabora included per user.</span>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap" data-testid="souvera-mail-quota-row">
                                <span className="text-[11px] text-zinc-500">Total mail storage (GB):</span>
                                <input
                                    type="number"
                                    min="1"
                                    data-testid="souvera-mail-quota-input"
                                    className="cm-input text-xs w-24 py-1"
                                    placeholder="50"
                                    value={mailQuota}
                                    onChange={(e) => setMailQuota(e.target.value)}
                                />
                                <span className="text-[11px] text-zinc-600">Required — total quota (default 50 GB) which the customer distributes across mailboxes in Souvera Central.</span>
                            </div>
                        </div>
                    )}
                    {status === "active" && <MailHealthBlock cloudId={cloudId} />}
                    {stepperVisible && (
                        <div className="mt-2 border border-[#3a2a5a] bg-black/30 rounded p-2" data-testid="souvera-provision-steps">
                            <div className="flex items-center justify-between mb-1.5">
                                <span className="text-[11px] text-zinc-500 uppercase tracking-wide">Provisioning — live</span>
                                <span className="text-[10px] text-zinc-600 font-mono">{stream ? "● SSE" : "…"}</span>
                            </div>
                            <div className="space-y-1">
                                {derivedSteps.map((s) => (
                                    <div key={s.key} className="flex items-center gap-2 text-xs"
                                        data-testid={`souvera-step-${s.key}`} data-status={s.status}>
                                        {s.status === "done" && <CheckCircle2 size={12} className="text-emerald-400 flex-shrink-0" />}
                                        {s.status === "running" && <Loader2 size={12} className="text-[#b88aff] animate-spin flex-shrink-0" />}
                                        {s.status === "error" && <XCircle size={12} className="text-red-400 flex-shrink-0" />}
                                        {s.status === "pending" && <Circle size={12} className="text-zinc-600 flex-shrink-0" />}
                                        <span className={
                                            s.status === "done" ? "text-zinc-300"
                                                : s.status === "running" ? "text-[#b88aff]"
                                                    : s.status === "error" ? "text-red-400"
                                                        : "text-zinc-600"
                                        }>{s.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {(data.dns_records || []).length > 0 && (
                        <details className="mt-2" data-testid="souvera-dns-records">
                            <summary className="text-[11px] text-cm-brand cursor-pointer">
                                {data.dns_records.length} DNS records to add (MX/SPF/DKIM/DMARC)
                            </summary>
                            <div className="mt-1.5 space-y-1">
                                {data.dns_records.map((r, i) => (
                                    <div key={i} className="text-[11px] font-mono bg-black/30 rounded px-2 py-1 break-all">
                                        <span className="text-amber-300">{r.type}</span>{" "}
                                        <span className="text-zinc-300">{r.name}</span>{" → "}
                                        <span className="text-zinc-400">{r.value}</span>
                                        {r.note && <span className="text-zinc-600"> ({r.note})</span>}
                                    </div>
                                ))}
                            </div>
                        </details>
                    )}
                    {data.dmarc_provider && (
                        <div className="mt-2 border border-[#3a2a5a] bg-black/30 rounded px-2 py-1.5 text-[11px]" data-testid="souvera-dmarc-provider">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-zinc-400">DMARC-Reporting (provider.tools):</span>
                                {data.dmarc_provider.verified ? (
                                    <span className="text-emerald-400" data-testid="souvera-dmarc-verified">verified ✔</span>
                                ) : data.dmarc_provider.enabled ? (
                                    <span className="text-amber-400" data-testid="souvera-dmarc-pending">registered — DNS verification pending</span>
                                ) : (
                                    <span className="text-red-400" data-testid="souvera-dmarc-failed">Registration failed</span>
                                )}
                                {data.dmarc_provider.policy && (
                                    <span className="text-zinc-600 font-mono">p={data.dmarc_provider.policy}</span>
                                )}
                            </div>
                            {data.dmarc_provider.last_error && (
                                <div className="text-zinc-600 mt-0.5 break-all" data-testid="souvera-dmarc-error">{data.dmarc_provider.last_error}</div>
                            )}
                        </div>
                    )}
                    {status === "requested" && (
                        <div className="text-[11px] text-amber-400 mt-1.5" data-testid="souvera-pending-note">
                            Requested — Stalwart VM provisioning + app installation follow.
                        </div>
                    )}
                    {enabled && status !== "deprovisioning" && (
                    <details className="mt-2" data-testid="souvera-actions">
                        <summary className="text-[11px] text-zinc-500 uppercase tracking-wide cursor-pointer select-none">
                            Actions
                        </summary>
                        <div className="flex flex-wrap items-center gap-2 mt-1.5">
                    {status === "active" && (
                        <button
                            data-testid="souvera-pmg-resync-btn"
                            onClick={pmgResync}
                            disabled={busy}
                            title="Re-set the relay domain + transport in the Proxmox Mail Gateway (e.g. after a Stalwart IP change or failed inbox setup)"
                            className="text-[11px] px-2.5 py-1 border border-[#3a5a7a] text-[#88c0ff] hover:bg-[#0f1a2a] disabled:opacity-50"
                        >
                            {busy ? "…" : "Re-apply PMG routing"}
                        </button>
                    )}
                    {status === "active" && (
                        <button
                            data-testid="souvera-dmarc-register-btn"
                            onClick={dmarcRegister}
                            disabled={busy}
                            title="Register the mail domain with provider.tools for DMARC reporting (idempotent — fetches an existing registration). The DMARC record is replaced + a verification TXT added; then publish in DNS."
                            className="text-[11px] px-2.5 py-1 border border-[#3a5a7a] text-[#88c0ff] hover:bg-[#0f1a2a] disabled:opacity-50"
                        >
                            {busy ? "…" : "Register DMARC"}
                        </button>
                    )}
                    {status === "active" && data.dmarc_provider?.enabled && !data.dmarc_provider?.verified && (
                        <button
                            data-testid="souvera-dmarc-verify-btn"
                            onClick={dmarcVerify}
                            disabled={busy}
                            title="Have provider.tools verify the domain now (after the verification TXT + DMARC record are live in DNS). Otherwise runs automatically every ~15 min."
                            className="text-[11px] px-2.5 py-1 border border-[#3a6a4a] text-[#7fd99a] hover:bg-[#0f1f15] disabled:opacity-50"
                        >
                            {busy ? "…" : "Verify DMARC"}
                        </button>
                    )}
                    {enabled && status !== "deprovisioning" && (
                        <button
                            data-testid="souvera-reprovision-btn"
                            onClick={reprovision}
                            disabled={busy}
                            title="Run provisioning again (e.g. after abort or error). Idempotent — S3 bucket, DB and a reachable Stalwart VM are reused; orphaned VMs are replaced."
                            className="text-[11px] px-2.5 py-1 border border-[#5a3a7a] text-[#b88aff] hover:bg-[#1a0f2a] disabled:opacity-50"
                        >
                            {busy ? "…" : "Reprovision"}
                        </button>
                    )}
                    {status === "active" && (
                        <div className="flex items-center gap-1">
                            <select
                                value={updateChannel}
                                onChange={(e) => setUpdateChannel(e.target.value)}
                                disabled={busy}
                                data-testid="souvera-update-channel-select"
                                className="text-[11px] px-1.5 py-1 bg-zinc-800 border border-zinc-700 text-zinc-300 font-mono rounded-sm"
                            >
                                <option value="dev">Dev</option>
                                <option value="stable">Stable</option>
                            </select>
                            <button
                                data-testid="souvera-update-apps-btn"
                                onClick={updateApps}
                                disabled={busy}
                                title="Update souvera_mail / souvera_shield / souvera_central — code push to the worker (via agent token), occ migration and re-activation. Channel: Dev (main) / Stable (release tag)."
                                className="text-[11px] px-2.5 py-1 border border-[#3a6a4a] text-[#7fd99a] hover:bg-[#0f1f15] disabled:opacity-50"
                            >
                                {busy ? "…" : "Update Souvera apps"}
                            </button>
                        </div>
                    )}
                    {status === "active" && (
                        <button
                            data-testid="souvera-harden-admins-btn"
                            onClick={hardenAdmins}
                            disabled={busy}
                            title="Harden the technical ncadmin, create/repair the customer admin scadmin (group souvera-admins, no superadmin) and restrict Desk/Link to souvera-users + souvera-admins (idempotent)."
                            className="text-[11px] px-2.5 py-1 border border-[#5a3a7a] text-[#b88aff] hover:bg-[#1a0f2a] disabled:opacity-50"
                        >
                            {busy ? "…" : "Sync admins & groups"}
                        </button>
                    )}
                    {status === "active" && (
                        <button
                            data-testid="souvera-mail-migrate-btn"
                            onClick={mailMigrate}
                            disabled={busy}
                            title="Migrate the legacy Souvera Stalwart (v0.13.4) into this cloud: create new DB + bucket, copy legacy data (SOURCE read-only), v0.13→v0.16, add final config (relay→PMG / OIDC). Idempotent/resumable."
                            className="text-[11px] px-2.5 py-1 border border-[#7a5a3a] text-[#ffcf88] hover:bg-[#1f150a] disabled:opacity-50"
                        >
                            {busy ? "…" : "Mail migration (legacy Souvera)"}
                        </button>
                    )}
                    {status === "active" && (
                        <button
                            data-testid="souvera-postmaster-backfill-btn"
                            onClick={postmasterBackfill}
                            disabled={busy}
                            title="Idempotently backfill the postmaster@ app mailbox (500 MB) + no-reply alias, scadmin quota (500 MB), NC mailout (sender no-reply@) and app password/total mail storage. For already-deployed Souvera clouds."
                            className="text-[11px] px-2.5 py-1 border border-[#3a5a7a] text-[#88bfff] hover:bg-[#0a151f] disabled:opacity-50"
                        >
                            {busy ? "…" : "Postmaster backfill"}
                        </button>
                    )}
                        </div>
                    </details>
                    )}
                    {err && (
                        <div className="text-[11px] text-red-400 font-mono mt-1.5 break-all" data-testid="souvera-err">
                            {err}
                        </div>
                    )}
                    {enabled && (
                        <div className="mt-2 flex items-start gap-1.5 text-[11px] text-zinc-500" data-testid="souvera-locked-note">
                            <Lock size={11} className="text-zinc-500 mt-0.5 flex-shrink-0" />
                            <span>Souvera Workspace is locked after activation — the primary domain &amp; all service URLs are permanently bound. Removal only happens by deleting the cloud.</span>
                        </div>
                    )}
                </div>
                {enabled ? (
                    <span
                        data-testid="souvera-locked-badge"
                        title="Souvera Workspace cannot be disabled after activation"
                        className="text-[11px] px-3 py-1.5 border border-zinc-700 text-zinc-500 flex items-center gap-1 cursor-not-allowed flex-shrink-0"
                    >
                        <Lock size={11} /> {inFlight ? "…" : "Locked"}
                    </span>
                ) : (
                    <button
                        data-testid="souvera-toggle-btn"
                        onClick={enable}
                        disabled={busy || inFlight || !cloudActive || !domain.trim() || !slugNorm || !(Number(souveraSeats) >= 1)}
                        title={!cloudActive ? "Cloud must be active to enable Souvera Workspace" : (!(Number(souveraSeats) >= 1) ? "Souvera user count is required (min. 1)" : undefined)}
                        className="text-[11px] px-3 py-1.5 border border-[#7a5aff] text-[#b88aff] hover:bg-[#1a0f3a] disabled:opacity-50 flex-shrink-0"
                    >
                        {busy || inFlight ? "…" : "Enable"}
                    </button>
                )}
            </div>
        </div>
    );
}


/**
 * Per-cloud "extra services" toggles. Each service line shows the current
 * binding + an Enable/Disable button. Activation now runs through the
 * `cloud.service` JOB QUEUE (Phase A): the toggle enqueues a tracked job that
 * waits until the cloud is `active`, applies the binding/OCC config, and only
 * reports "done" once the agent confirms it applied the new config version.
 *
 * Talk HPB is BUNDLED — one toggle installs `spreed`, configures signaling,
 * registers all TURN servers from the bound pool.
 */
const PHASE_LABEL = {
    wait_active: "waiting for cloud",
    await_agent: "applying on agent",
    apply: "applying",
    confirmed: "confirmed",
    done: "confirmed",
    queued: "queued",
};

function JobStatusBadge({ job }) {
    if (!job) return null;
    const action = job.payload?.action || "";
    const phase = PHASE_LABEL[job.current_step] || PHASE_LABEL[job.phase] || job.current_step || "";
    let cfg;
    if (job.status === "queued") {
        cfg = { Icon: Clock, cls: "bg-amber-950 border-amber-800 text-amber-300", label: "QUEUED" };
    } else if (job.status === "running") {
        cfg = { Icon: Loader2, cls: "bg-sky-950 border-sky-800 text-sky-300", label: "RUNNING", spin: true };
    } else if (job.status === "succeeded") {
        cfg = { Icon: CheckCircle2, cls: "bg-emerald-950 border-emerald-800 text-emerald-300", label: "DONE" };
    } else if (job.status === "failed") {
        cfg = { Icon: XCircle, cls: "bg-red-950 border-red-800 text-red-300", label: "FAILED" };
    } else {
        cfg = { Icon: Clock, cls: "bg-zinc-900 border-zinc-700 text-zinc-300", label: (job.status || "").toUpperCase() };
    }
    const { Icon } = cfg;
    return (
        <span
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 border text-[10px] font-mono ${cfg.cls}`}
            title={job.error || `${action} · ${phase}`}
            data-testid={`cloud-service-${job.payload?.service}-job-status`}
        >
            <Icon size={9} className={cfg.spin ? "animate-spin" : ""} />
            {cfg.label}
            {action ? ` ${action}` : ""}
            {cfg.label === "RUNNING" && phase ? ` · ${phase}` : ""}
        </span>
    );
}

const APPAPI_STATUS = {
    off: { cls: "cm-badge-neutral", label: "OFF" },
    disabled: { cls: "cm-badge-neutral", label: "OFF" },
    provisioning: { cls: "bg-sky-950 border-sky-800 text-sky-300", label: "PROVISIONING" },
    deprovisioning: { cls: "bg-amber-950 border-amber-800 text-amber-300", label: "REMOVING…" },
    active: { cls: "cm-badge-success", label: "ACTIVE" },
    failed: { cls: "bg-red-950 border-red-800 text-red-300", label: "ERROR" },
};

/**
 * AppAPI / Externe Apps (HaRP Deploy-Daemon). Provisions a dedicated, per-cloud
 * Docker+HaRP VM (operator-chosen size) and registers it fully automatically as
 * the DEFAULT ExApp deploy daemon in this cloud's Nextcloud. Polls the status
 * endpoint so the card reflects provisioning → active live.
 */
function AppApiServiceLine({ cloudId, cloud, onChange }) {
    const cloudActive = cloud.status === "active";
    const { data, mutate } = useSWR(
        `/api/admin/clouds/${cloudId}/services/appapi`, fetcher,
        { refreshInterval: 5000 });
    const { data: sizes } = useSWR(`/api/admin/infra/appapi/vm-sizes`, fetcher);
    const [sizeId, setSizeId] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    useEffect(() => {
        if (data?.size_id) setSizeId(data.size_id);
        else if (sizes?.length && !sizeId) setSizeId(sizes[0].id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data?.size_id, sizes]);

    const status = data?.status || "off";
    const enabled = !!data?.enabled;
    const inFlight = status === "provisioning" || status === "deprovisioning";
    const statusCfg = APPAPI_STATUS[status] || { cls: "cm-badge-neutral", label: (status || "").toUpperCase() };
    const sizeLabel = (sizes || []).find((s) => s.id === (data?.size_id || sizeId));

    const call = async (path, body) => {
        setBusy(true); setErr(null);
        try {
            await api.post(`/api/admin/clouds/${cloudId}/services/appapi/${path}`, body);
            await mutate();
            if (onChange) await onChange();
        } catch (e) { setErr(formatApiError(e)); }
        setBusy(false);
    };
    const enable = () => call("enable", { size_id: sizeId });
    const disable = () => {
        if (!window.confirm(
            "Disable AppAPI?\n\nThe HaRP daemon VM is removed and the deploy " +
            "daemon is deregistered in Nextcloud. Installed external apps " +
            "(ExApps) become unreachable as a result.")) return;
        call("disable");
    };
    const reprovision = () => call("reprovision");

    return (
        <div className="bg-zinc-950 border border-[#1f3a4a] p-3" data-testid="cloud-service-appapi">
            <div className="flex items-start gap-2">
                <Puzzle size={14} className="text-cyan-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm text-zinc-200">AppAPI / External Apps</span>
                        <span className={`cm-badge ${statusCfg.cls} text-[10px]`} data-testid="appapi-status-badge">
                            {statusCfg.label}
                        </span>
                        {inFlight && <Loader2 size={12} className="animate-spin text-sky-400" />}
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-1 leading-relaxed">
                        Dedicated, per-cloud isolated HaRP deploy-daemon VM (Docker)
                        — registered fully automatically in Nextcloud as the default daemon.
                        External apps (ExApps) can then be installed in the NC AppAPI settings.
                    </div>

                    {enabled && (
                        <div className="text-[11px] text-zinc-400 font-mono mt-1.5 space-y-0.5">
                            {data?.vm_ip && (
                                <div data-testid="appapi-vm-ip">
                                    HaRP-VM: <span className="text-cyan-300">{data.vm_ip}:8780</span>
                                </div>
                            )}
                            {sizeLabel && (
                                <div data-testid="appapi-size">
                                    Size: <span className="text-zinc-300">{sizeLabel.label}</span>
                                    {" "}({sizeLabel.cpu} vCPU / {sizeLabel.ram_mb} MB / {sizeLabel.disk_gb} GB)
                                </div>
                            )}
                            {data?.daemon_name && (
                                <div data-testid="appapi-daemon-name">
                                    Daemon: <span className="text-zinc-300">{data.daemon_name}</span>
                                    {" · "}
                                    {data.daemon_registered
                                        ? <span className="text-emerald-400">registered</span>
                                        : <span className="text-amber-400">not confirmed</span>}
                                </div>
                            )}
                        </div>
                    )}

                    {!enabled && (
                        <div className="flex items-center gap-2 mt-2" data-testid="appapi-size-row">
                            <span className="text-[11px] text-zinc-500">VM size:</span>
                            <select
                                data-testid="appapi-size-select"
                                value={sizeId}
                                onChange={(e) => setSizeId(e.target.value)}
                                disabled={!cloudActive || busy}
                                className="cm-input text-xs py-0.5"
                            >
                                {(sizes || []).map((s) => (
                                    <option key={s.id} value={s.id}>
                                        {s.label} — {s.cpu}c / {Math.round(s.ram_mb / 1024)}GB / {s.disk_gb}GB
                                    </option>
                                ))}
                            </select>
                            {!cloudActive && (
                                <span className="text-[11px] text-amber-400" data-testid="appapi-needs-active">
                                    Cloud must be active
                                </span>
                            )}
                        </div>
                    )}

                    {err && (
                        <div className="text-[11px] text-red-400 font-mono mt-1.5 break-all" data-testid="appapi-err">
                            {err}
                        </div>
                    )}
                </div>

                <div className="flex flex-col gap-1.5 items-stretch">
                    {!enabled ? (
                        <button
                            data-testid="appapi-enable-btn"
                            onClick={enable}
                            disabled={!cloudActive || busy || !sizeId}
                            title={!cloudActive ? "Cloud must be active to enable AppAPI" : undefined}
                            className="text-[11px] px-3 py-1.5 border border-cyan-700 text-cyan-300 hover:bg-cyan-950/40 disabled:opacity-50"
                        >
                            {busy ? "…" : "Enable"}
                        </button>
                    ) : (
                        <button
                            data-testid="appapi-disable-btn"
                            onClick={disable}
                            disabled={busy || inFlight}
                            className="text-[11px] px-3 py-1.5 border border-red-800 text-red-300 hover:bg-red-950 disabled:opacity-50"
                        >
                            {busy ? "…" : "Disable"}
                        </button>
                    )}
                    {enabled && (
                        <button
                            data-testid="appapi-reprovision-btn"
                            onClick={reprovision}
                            disabled={busy || inFlight}
                            title="Re-provision the HaRP VM + re-register the daemon (idempotent — a reachable VM is reused)."
                            className="text-[11px] px-3 py-1.5 border border-amber-700 text-amber-300 hover:bg-amber-950/40 disabled:opacity-50 flex items-center gap-1 justify-center"
                        >
                            <RefreshCw size={10} /> {busy ? "…" : "Reprovision"}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function VmResizePanel({ cloudId }) {
    const [cpu, setCpu] = useState("");
    const [ramMb, setRamMb] = useState("");
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(null);
    const [err, setErr] = useState(null);

    const doResize = async () => {
        const payload = {};
        const c = parseInt(cpu, 10);
        const r = parseInt(ramMb, 10);
        if (c > 0) payload.cpu = c;
        if (r > 0) payload.ram_mb = r;
        if (!payload.cpu && !payload.ram_mb) return;
        if (!confirm("VM will be stopped, CPU/RAM adjusted and restarted. About 30s downtime. Continue?")) return;
        setBusy(true); setErr(null); setMsg(null);
        try {
            const { data } = await api.put(`/api/admin/clouds/${cloudId}/services/collabora/dedicated/resize`, payload);
            setMsg(data.message || "Resized.");
            setCpu(""); setRamMb("");
        } catch (e) {
            setErr(formatApiError(e));
        }
        setBusy(false);
    };

    return (
        <div className="px-5 py-3 border-t border-zinc-800">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono mb-2">VM Resize (CPU / RAM)</div>
            <div className="flex gap-2">
                <input type="number" min={1} max={32} value={cpu} onChange={(e) => setCpu(e.target.value)}
                    placeholder="CPU Cores" className="cm-input flex-1 font-mono text-[11px] py-1" />
                <input type="number" min={512} max={65536} step={256} value={ramMb} onChange={(e) => setRamMb(e.target.value)}
                    placeholder="RAM MB" className="cm-input flex-1 font-mono text-[11px] py-1" />
                <button onClick={doResize} disabled={busy || (!cpu && !ramMb)}
                    className="text-[11px] px-3 py-1 border border-amber-700 text-amber-300 hover:bg-amber-950/40 disabled:opacity-50 whitespace-nowrap">
                    {busy ? "…" : "Resize + Reboot"}
                </button>
            </div>
            {msg && <div className="text-emerald-300 text-[11px] font-mono mt-1">{msg}</div>}
            {err && <div className="text-red-400 text-[11px] font-mono break-all mt-1">{err}</div>}
        </div>
    );
}


function CollaboraConfigEditor({ cloudId }) {
    const [open, setOpen] = useState(false);
    const [cfg, setCfg] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState(null);
    const [err, setErr] = useState(null);

    const loadConfig = async () => {
        setLoading(true); setErr(null);
        try {
            const { data } = await api.get(`/api/admin/clouds/${cloudId}/services/collabora/dedicated/config`);
            setCfg(data);
        } catch (e) {
            setErr(formatApiError(e));
        }
        setLoading(false);
    };
    const saveConfig = async () => {
        setSaving(true); setErr(null); setMsg(null);
        const payload = {};
        for (const [k, v] of Object.entries(cfg || {})) {
            if (k === "dictionaries") {
                if (Array.isArray(v)) payload[k] = v;
            } else if (v !== null && v !== "" && v !== undefined) {
                payload[k] = v;
            }
        }
        try {
            await api.put(`/api/admin/clouds/${cloudId}/services/collabora/dedicated/config`, payload);
            setMsg("✓ Saved — coolwsd is restarted.");
        } catch (e) {
            setErr(formatApiError(e));
        }
        setSaving(false);
    };

    const openModal = () => { setOpen(true); if (!cfg) loadConfig(); };

    return (
        <>
            <button
                data-testid="cloud-service-dedicated-collabora-config-btn"
                onClick={openModal}
                disabled={loading}
                className="text-[11px] px-3 py-1.5 border border-sky-700 text-sky-300 hover:bg-sky-950/40 disabled:opacity-50"
            >
                Config
            </button>
            {open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={() => setOpen(false)}>
                    <div className="bg-zinc-950 border border-zinc-700 w-full max-w-2xl mx-4 rounded-lg shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="dedicated-collabora-config-modal">
                        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
                            <span className="text-xs font-mono uppercase tracking-widest text-zinc-300">coolwsd.xml — Collabora Config</span>
                            <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-zinc-300">
                                <X size={14} />
                            </button>
                        </div>
                        <div className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
                            {cfg ? (
                                <>
                                    {/* Numeric + text fields: 3-column grid */}
                                    <div className="grid grid-cols-3 gap-x-4 gap-y-3">
                                        {FIELDS.filter(f => f.type !== "textarea").map((f) => (
                                            <div key={f.key}>
                                                <label className="block text-[11px] text-zinc-500 font-mono mb-1">{f.label}</label>
                                                <input
                                                    type={f.type || "text"}
                                                    value={cfg[f.key] ?? ""}
                                                    onChange={(e) => setCfg({ ...cfg, [f.key]: e.target.value === "" ? null : (f.type === "number" ? Number(e.target.value) : e.target.value) })}
                                                    className="cm-input w-full font-mono text-[11px] py-1"
                                                    placeholder={f.placeholder}
                                                />
                                            </div>
                                        ))}
                                    </div>

                                    {/* Textarea fields: full width */}
                                    {FIELDS.filter(f => f.type === "textarea").map((f) => (
                                        <div key={f.key}>
                                            <label className="block text-[11px] text-zinc-500 font-mono mb-1">{f.label}</label>
                                            <textarea
                                                value={cfg[f.key] ?? ""}
                                                onChange={(e) => setCfg({ ...cfg, [f.key]: e.target.value })}
                                                className="cm-input w-full font-mono text-[11px] py-1.5 min-h-[56px]"
                                                placeholder={f.placeholder}
                                                rows={3}
                                            />
                                        </div>
                                    ))}

                                    {/* Wörterbücher */}
                                    <div className="pt-3 border-t border-zinc-800">
                                        <label className="block text-[11px] text-zinc-500 font-mono mb-2">Dictionaries (Hunspell)</label>
                                        <div className="grid grid-cols-3 gap-x-4 gap-y-1">
                                            {DICT_OPTIONS.map((d) => {
                                                const current = Array.isArray(cfg.dictionaries) ? cfg.dictionaries : (cfg.dictionaries || "").split(",").map(s => s.trim()).filter(Boolean);
                                                const checked = current.includes(d.code);
                                                return (
                                                    <label key={d.code} className="flex items-center gap-1.5 text-[11px] font-mono text-zinc-300 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={checked}
                                                            onChange={() => {
                                                                const next = checked
                                                                    ? current.filter(c => c !== d.code)
                                                                    : [...current, d.code];
                                                                setCfg({ ...cfg, dictionaries: next });
                                                            }}
                                                            className="accent-[#CCFF00]"
                                                        />
                                                        {d.label}
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="text-zinc-500 text-xs py-6 text-center">{loading ? "Loading config…" : "Error loading."}</div>
                            )}
                            {msg && <div className="text-emerald-300 text-[11px] font-mono">{msg}</div>}
                            {err && <div className="text-red-400 text-[11px] font-mono break-all">{err}</div>}
                        </div>
                        {cfg && (
                            <div className="px-5 py-3 border-t border-zinc-800 flex justify-between items-center">
                                <span className="text-[10px] text-zinc-600 font-mono">Changes require a coolwsd restart</span>
                                <button onClick={saveConfig} disabled={saving || !cfg}
                                    className="cm-btn-primary text-[11px] px-4 py-1.5">
                                    {saving ? "Saving…" : "Save + restart"}
                                </button>
                            </div>
                        )}
                        {cfg && <VmResizePanel cloudId={cloudId} />}
                    </div>
                </div>
            )}
        </>
    );
}

const FIELDS = [
    { key: "max_file_size_mb", label: "Max File Size (MB)", type: "number", placeholder: "100" },
    { key: "idle_timeout_secs", label: "Idle Timeout (s)", type: "number", placeholder: "3600" },
    { key: "idle_save_duration_secs", label: "Idle Save Duration (s)", type: "number", placeholder: "30" },
    { key: "max_concurrency", label: "Max Concurrency", type: "number", placeholder: "4" },
    { key: "autosave_interval_secs", label: "Autosave Interval (s)", type: "number", placeholder: "300" },
    { key: "watermark_text", label: "Watermark Text", placeholder: "{username}" },
    { key: "watermark_opacity", label: "Watermark Opacity", placeholder: "0.2" },
    { key: "logging_level", label: "Log Level", placeholder: "warning" },
    { key: "font_urls", label: "Font URLs (one per line)", type: "textarea", placeholder: "https://example.com/fonts/MyFont.ttf" },
];

const DICT_OPTIONS = [
    { code: "de", label: "German (de)" },
    { code: "en", label: "English (en-US)" },
    { code: "en-gb", label: "English (en-GB)" },
    { code: "fr", label: "French (fr)" },
    { code: "es", label: "Spanish (es)" },
    { code: "it", label: "Italian (it)" },
    { code: "nl", label: "Dutch (nl)" },
];


export default function CloudServicesPanel({ cloudId, cloud, onChange }) {
    const [busy, setBusy] = useState(null);
    const [err, setErr] = useState(null);
    const [turnTest, setTurnTest] = useState(null);  // {loading, data}

    // Sold-seat counts (stored only for now, no enforcement). Collabora is sold
    // per user; when Souvera is enabled its per-user count includes Collabora,
    // so the Collabora seat field is shown as "included in Souvera".
    const souveraOn = !!cloud.souvera_workspace_enabled;
    const [collabSeats, setCollabSeats] = useState(cloud.collabora_user ?? "");
    const [seatBusy, setSeatBusy] = useState(false);
    useEffect(() => { setCollabSeats(cloud.collabora_user ?? ""); }, [cloud.collabora_user]);
    const saveSeats = async (payload) => {
        setSeatBusy(true);
        setErr(null);
        try {
            await api.post(`/api/admin/clouds/${cloudId}/service-seats`, payload);
            if (onChange) await onChange();
        } catch (e) {
            setErr(formatApiError(e));
        }
        setSeatBusy(false);
    };

    const testTurn = async () => {
        setTurnTest({ loading: true, data: null });
        try {
            const r = await api.post(`/api/admin/clouds/${cloudId}/test-turn`);
            setTurnTest({ loading: false, data: r.data });
        } catch (e) {
            setTurnTest({ loading: false, data: { ok: false, summary: formatApiError(e), results: [] } });
        }
    };

    const handleDedicatedEnable = async () => {
        setBusy("dedicated-collabora"); setErr(null);
        try {
            await api.post(`/api/admin/clouds/${cloudId}/services/collabora/dedicated`);
            if (onChange) await onChange();
        } catch (e) {
            setErr(formatApiError(e));
        }
        setBusy(null);
    };

    const handleDedicatedDisable = async () => {
        if (!confirm("Really remove Dedicated Collabora?")) return;
        setBusy("dedicated-collabora"); setErr(null);
        try {
            await api.delete(`/api/admin/clouds/${cloudId}/services/collabora/dedicated`);
            if (onChange) await onChange();
        } catch (e) {
            setErr(formatApiError(e));
        }
        setBusy(null);
    };

    // Poll the cloud's service-activation jobs so each line can show
    // queued/running/done/failed live as the queue processes them.
    const { data: jobs, mutate: mutateJobs } = useSWR(
        `/api/admin/jobs?cloud_id=${cloudId}&type=cloud.service&limit=40`,
        fetcher,
        { refreshInterval: 4000 },
    );

    // jobs come back newest-first → first match per service is the latest.
    const latestByService = {};
    (jobs || []).forEach((j) => {
        const s = j.payload?.service;
        if (s && !latestByService[s]) latestByService[s] = j;
    });

    const toggle = async (service, bound) => {
        setBusy(service);
        setErr(null);
        try {
            const desired = !bound;
            const payload = { enabled: desired };
            // Collabora seats are mandatory (>=1) when enabling — send the
            // current input so the backend persists the booked count.
            if (service === "collabora" && desired) {
                payload.collabora_user = collabSeats === "" ? null : Number(collabSeats);
            }
            await api.put(`/api/admin/clouds/${cloudId}/services/${service}`, payload);
            await mutateJobs();
            if (onChange) await onChange();
        } catch (e) {
            setErr(formatApiError(e));
        }
        setBusy(null);
    };

    const reconfigure = async (service) => {
        if (!confirm("Rewrite the configuration for this service? The worker agent applies it on the next heartbeat (~30s).")) return;
        setBusy(service);
        setErr(null);
        try {
            await api.post(`/api/admin/clouds/${cloudId}/services/${service}/reconfigure`);
            await mutateJobs();
            if (onChange) await onChange();
        } catch (e) {
            setErr(formatApiError(e));
        }
        setBusy(null);
    };

    const services = [
        {
            key: "collabora",
            label: "Collabora Online",
            icon: FileText,
            description: "Office suite (docx/xlsx/pptx). Enable = installs `richdocuments` + WOPI config + pool binding.",
            bound: !!cloud.collabora_member_id,
            poolId: cloud.collabora_pool_id,
            memberId: cloud.collabora_member_id,
        },
        {
            key: "dedicated-collabora",
            label: "Dedicated Collabora",
            icon: FileText,
            description: "Premium: isolated Collabora VM (licensed fonts, custom config).",
            bound: !!cloud.collabora_dedicated,
            dedicated: true,
            vmId: cloud.collabora_vm_id,
            vmIp: cloud.collabora_dedicated_ip,
        },
        {
            key: "notify-push",
            label: "Notify Push",
            icon: Zap,
            description: "Real-time Files-app updates. Enable = installs `notify_push` + runs `notify_push:setup`.",
            bound: !!(cloud.notify_push_member_id || cloud.hpb_member_id),
            poolId: cloud.notify_push_pool_id || cloud.hpb_pool_id,
            memberId: cloud.notify_push_member_id || cloud.hpb_member_id,
        },
        {
            key: "talk-hpb",
            label: "Talk HPB (Calls)",
            icon: Mic,
            description: "Calls with hundreds of users. Enable = installs `spreed` + Signaling SFU URL + all TURN servers — one click.",
            bound: !!cloud.talk_hpb_pool_id,
            poolId: cloud.talk_hpb_pool_id,
            memberId: null,
        },
        {
            key: "recording",
            label: "Talk Recording",
            icon: Video,
            description: "Call recording. Enable = writes `recording_servers` in NC Talk (backend from the Talk HPB pool). Requires active Talk HPB + a talk-recording VM in the pool.",
            bound: !!cloud.recording_enabled,
            poolId: cloud.talk_hpb_pool_id,
            memberId: null,
            requiresTalkHpb: true,
        },
        {
            key: "whiteboard",
            label: "Whiteboard",
            icon: Layout,
            description: "Collaborative drawing for Talk + Files. Enable = installs `whiteboard` app + JWT secret + server URL.",
            bound: !!cloud.whiteboard_enabled,
            poolId: null,
            memberId: null,
        },
        {
            key: "antivirus",
            label: "Antivirus (ICAP/ClamAV)",
            icon: ShieldCheck,
            description: "Central virus scanning via the avscan LB. Enable = installs `files_antivirus` (ICAP mode) and pins av_host to the avscan LB IP:1344. Requires an active avscan LB + at least one avscan VM (Infrastructure → Service VMs).",
            bound: !!cloud.antivirus_enabled,
            poolId: null,
            memberId: null,
        },
    ];

    return (
        <div className="cm-card p-5" data-testid="cloud-services-panel">
            <div className="cm-h3 mb-3">Services</div>
            <div className="text-xs text-zinc-500 mb-3 leading-relaxed">
                Activations run through the job queue — they wait until the cloud
                is <span className="text-zinc-300">active</span>, then apply via OCC
                on the worker agent and confirm when done. Track progress below or
                in <span className="text-zinc-300">Recent jobs</span>.
            </div>
            <div className="space-y-3">
                {services.map((s) => {
                    const Icon = s.icon;
                    const job = latestByService[s.key];
                    const pending = job && (job.status === "queued" || job.status === "running");
                    const recDepBlocked = s.requiresTalkHpb && !cloud.talk_hpb_pool_id && !s.bound;
                    return (
                        <div
                            key={s.key}
                            className="bg-zinc-950 border border-zinc-800 p-3"
                            data-testid={`cloud-service-${s.key}`}
                        >
                            <div className="flex items-start gap-2">
                                <Icon size={14} className="text-zinc-400 mt-0.5 flex-shrink-0" />
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-mono text-sm text-zinc-200">{s.label}</span>
                                        {s.dedicated ? (
                                            s.bound ? <span className="cm-badge cm-badge-success text-[10px]" data-testid={`cloud-service-${s.key}-status-on`}>DEDICATED</span>
                                                : <span className="cm-badge cm-badge-neutral text-[10px]" data-testid={`cloud-service-${s.key}-status-off`}>PREMIUM</span>
                                        ) : s.bound ? (
                                            <span className="cm-badge cm-badge-success text-[10px]" data-testid={`cloud-service-${s.key}-status-on`}>
                                                BOUND
                                            </span>
                                        ) : (
                                            <span className="cm-badge cm-badge-neutral text-[10px]" data-testid={`cloud-service-${s.key}-status-off`}>
                                                NOT BOUND
                                            </span>
                                        )}
                                        <JobStatusBadge job={job} />
                                    </div>
                                    <div className="text-[11px] text-zinc-500 mt-1 leading-relaxed">
                                        {s.description}
                                    </div>
                                    {recDepBlocked && (
                                        <div className="text-[11px] text-amber-400 mt-1" data-testid="cloud-service-recording-needs-talk-hpb">
                                            Talk HPB (signaling) must be enabled first.
                                        </div>
                                    )}
                                    {s.key === "collabora" && (
                                        souveraOn ? (
                                            <div className="text-[11px] text-zinc-500 mt-1.5" data-testid="collabora-seats-included">
                                                Sold users: <span className="text-[#b88aff]">included in Souvera</span>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-2 mt-1.5" data-testid="collabora-seats-row">
                                                <span className="text-[11px] text-zinc-500">Sold users:</span>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    data-testid="collabora-seats-input"
                                                    className="cm-input text-xs w-20 py-0.5"
                                                    placeholder="–"
                                                    value={collabSeats}
                                                    onChange={(e) => setCollabSeats(e.target.value)}
                                                />
                                                <button
                                                    data-testid="collabora-seats-save-btn"
                                                    onClick={() => saveSeats({ collabora_user: collabSeats === "" ? null : Number(collabSeats) })}
                                                    disabled={seatBusy}
                                                    className="text-[11px] px-2 py-0.5 border border-zinc-700 text-zinc-300 hover:bg-zinc-900 disabled:opacity-50"
                                                >
                                                    {seatBusy ? "…" : "Save"}
                                                </button>
                                            </div>
                                        )
                                    )}
                                    {s.dedicated && s.bound && (
                                        <div className="mt-1.5 text-xs text-zinc-400 font-mono" data-testid="dedicated-collabora-info">
                                            {s.vmId ? <div>VM: <span className="text-zinc-300">{s.vmId.slice(0, 16)}</span></div>
                                                : <div className="text-amber-400">Provisioning…</div>}
                                            {s.vmIp && <div>IP: <span className="text-zinc-300">{s.vmIp}</span></div>}
                                        </div>
                                    )}
                                    {s.bound && !s.dedicated && (
                                        <div className="text-[11px] text-zinc-400 font-mono mt-1.5">
                                            pool: <span className="text-zinc-300">{s.poolId?.slice(0, 12) || "—"}</span>
                                            {" · "}
                                            member: <span className="text-zinc-300">{s.memberId?.slice(0, 12) || "—"}</span>
                                        </div>
                                    )}
                                    {job && job.status === "failed" && job.error && (
                                        <div className="text-[11px] text-red-400 font-mono mt-1.5 break-all" data-testid={`cloud-service-${s.key}-job-error`}>
                                            {job.error}
                                        </div>
                                    )}
                                    {job && job.status === "running" && job.agent_progress && job.agent_progress.total > 0 && (
                                        <div
                                            className="text-[11px] font-mono mt-1.5"
                                            data-testid={`cloud-service-${s.key}-agent-progress`}
                                            title={
                                                (job.agent_progress.laggards || [])
                                                    .map((l) => `${l.role}#${l.vmid ?? "?"} @ v${l.applied}`)
                                                    .join(", ") || "all VMs in sync"
                                            }
                                        >
                                            <span
                                                className={
                                                    job.agent_progress.applied >= job.agent_progress.total
                                                        ? "text-emerald-400"
                                                        : "text-amber-400"
                                                }
                                            >
                                                {job.agent_progress.applied}/{job.agent_progress.total} VMs
                                            </span>
                                            <span className="text-zinc-500">
                                                {" "}applied config v{job.agent_progress.target}
                                            </span>
                                            {(job.agent_progress.laggards || []).length > 0 && (
                                                <span className="text-zinc-500">
                                                    {" · waiting on: "}
                                                    {(job.agent_progress.laggards || [])
                                                        .map((l) => l.role)
                                                        .join(", ")}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    {s.key === "talk-hpb" && turnTest?.data && (
                                        <div className="mt-2 p-2 rounded bg-zinc-900 border border-zinc-700" data-testid="cloud-service-talk-hpb-turn-result">
                                            <div className={`text-[11px] font-medium flex items-center gap-1 ${turnTest.data.ok ? "text-emerald-300" : "text-red-300"}`}>
                                                <Wifi size={10} /> TURN: {turnTest.data.ok ? "OK" : "Problem"} — {turnTest.data.summary}
                                            </div>
                                            {turnTest.data.has_secret === false && (turnTest.data.results || []).length > 0 && (
                                                <div className="text-[10px] text-amber-300 mt-0.5">⚠ No static-auth-secret — only STUN tested.</div>
                                            )}
                                            {(turnTest.data.results || []).map((r, i) => (
                                                <div key={i} className="text-[10px] font-mono flex items-start gap-1.5 mt-0.5" data-testid={`cloud-turn-uri-${i}`}>
                                                    <span className={r.stun && (r.turn === null || r.turn) ? "text-emerald-400" : "text-red-400"}>
                                                        {r.stun && (r.turn === null || r.turn) ? "✓" : "✗"}
                                                    </span>
                                                    <span className="text-zinc-300 break-all">{r.uri}</span>
                                                    <span className="text-zinc-500">— {r.detail || (r.stun ? "STUN ok" : "no STUN")}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="flex flex-col gap-1.5 items-stretch">
                                    {s.dedicated ? (
                                        <>
                                        {s.bound ? (
                                            <>
                                            <button onClick={handleDedicatedDisable} disabled={busy}
                                                className="text-[11px] px-3 py-1.5 border border-red-800 text-red-300 hover:bg-red-950 disabled:opacity-50">
                                                {busy ? "..." : "Destroy VM"}
                                            </button>
                                            <button
                                                data-testid="cloud-service-dedicated-collabora-reconfigure-btn"
                                                onClick={() => reconfigure("dedicated-collabora")}
                                                disabled={busy || pending}
                                                title="Bump config version → agent re-applies the Collabora configuration (WOPI, nginx, allowlist)"
                                                className="text-[11px] px-3 py-1.5 border border-amber-700 text-amber-300 hover:bg-amber-950/40 disabled:opacity-50"
                                            >
                                                Reconfigure
                                            </button>
                                            <CollaboraConfigEditor cloudId={cloudId} />
                                            </>
                                        ) : (
                                            <button onClick={handleDedicatedEnable} disabled={busy}
                                                className="text-[11px] px-3 py-1.5 border border-[#CCFF00] text-cm-brand hover:bg-[#10160a] disabled:opacity-50">
                                                {busy ? "..." : "Provision dedicated VM"}
                                            </button>
                                        )}
                                        </>
                                    ) : (
                                    <>
                                    <button
                                        data-testid={`cloud-service-${s.key}-toggle-btn`}
                                        onClick={() => toggle(s.key, s.bound)}
                                        disabled={busy === s.key || pending || recDepBlocked}
                                        title={recDepBlocked ? "Enable Talk HPB first" : undefined}
                                        className={`text-[11px] px-3 py-1.5 border ${
                                            s.bound
                                                ? "border-red-800 text-red-300 hover:bg-red-950"
                                                : "border-[#CCFF00] text-cm-brand hover:bg-[#10160a]"
                                        } disabled:opacity-50`}
                                    >
                                        {busy === s.key
                                            ? "…"
                                            : pending
                                                ? "pending…"
                                                : s.bound
                                                    ? "Disable"
                                                    : "Enable"}
                                    </button>
                                    {s.bound && (
                                        <button
                                            data-testid={`cloud-service-${s.key}-reconfigure-btn`}
                                            onClick={() => reconfigure(s.key)}
                                            disabled={busy === s.key || pending}
                                            title="Rewrite configuration + repair routes/secrets (without Disable→Enable)"
                                            className="text-[11px] px-3 py-1.5 border border-amber-700 text-amber-300 hover:bg-amber-950/40 disabled:opacity-50"
                                        >
                                            Reconfigure
                                        </button>
                                    )}
                                    </>
                                    )}
                                    {s.key === "talk-hpb" && s.bound && (
                                        <button
                                            data-testid="cloud-service-talk-hpb-test-turn-btn"
                                            onClick={testTurn}
                                            disabled={turnTest?.loading}
                                            title="Live test: STUN + authenticated TURN allocate against this cloud's pool"
                                            className="text-[11px] px-3 py-1.5 border border-sky-700 text-sky-300 hover:bg-sky-950/40 disabled:opacity-50 flex items-center gap-1 justify-center"
                                        >
                                            <Wifi size={10} /> {turnTest?.loading ? "Testing…" : "Test TURN"}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
            {/* Souvera: only show activation form in Advanced tab when NOT yet active.
                Once activated, full controls live in the dedicated Souvera tab. */}
            {!cloud?.souvera_workspace_enabled && (
                <div className="mt-3">
                    <SouveraWorkspaceLine cloudId={cloudId} cloud={cloud} onChange={onChange} compact />
                </div>
            )}
            <div className="mt-3">
                <AppApiServiceLine cloudId={cloudId} cloud={cloud} onChange={onChange} />
            </div>
            {err && (
                <div className="mt-3 text-red-400 text-xs font-mono break-all" data-testid="cloud-services-err">
                    {err}
                </div>
            )}
        </div>
    );
}
